import S from 's-js';
import { SArray as ArraySignal } from 's-array';
import * as moment from 'moment';
import TZDate from 'lib/TZDate';
import { IAjax } from 'lib/Ajax';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { groupBy } from 'lib/groupBy';

import { Entrypoints } from 'core/app/entrypoints';

import { PCCache } from 'data/PCCache';

import { MissingPilotJobOrdersResponse, MissingPilotJobOrdersResponseOptions, MissingPilotJobOrder } from 'data/models/MissingPilotJobOrders';
import { MissingPilotJobOrderType } from 'server/enums/MissingPilotJobOrderType';
import { PilotJobOrder, PilotJobOrderOptions } from 'data/models/PilotJobOrder';

import { DispatchDashboardCache } from './cache';
import { internalDateFormat } from './model';

export interface AddPilotJobOrderParams {
    pilotAgencyId : number;
    from : TZDate;
    to : TZDate;
    portCallId : number | null;
    template? : PilotJobOrder;
    onAddPilotJobOrder : (pjo : PilotJobOrder) => void;
    onCancel : () => void;
}

export function AddPilotJobOrder(
    loader : CachedModelLoader<MissingPilotJobOrdersResponse, MissingPilotJobOrdersResponseOptions>,
    ajax : IAjax,
    entrypoints : Entrypoints,
    data : PCCache,
    cache : DispatchDashboardCache,
    {
        pilotAgencyId,
        from,
        to,
        portCallId,
        template: _template,
        onAddPilotJobOrder,
        onCancel
    } : AddPilotJobOrderParams
) {
    const
        { model } = loader({ From: from.toJSON(), To: to.toJSON(), PortCallId: portCallId }),
        client = ajax.Url<PilotJobOrderOptions>(entrypoints.AddPilotJobOrder({ Id: pilotAgencyId })),
        days = groupBy(model.MissingPilotJobOrders, pjo => internalDateFormat(pjo.Date()))
            // Th 01/26/2017 -- Our date format of 'YYYY-MM-DD' is string sortable
            .orderBy(({ key: date }) => date)
            .map(({ key: date, items }) => MissingPJOsDayViewModel(date, items)),
        adding = S.data(false),
        template = _template || new PilotJobOrder({
            Id: 0,
            Billable: true
        });

    return {
        Days: days,
        AddPilotJobOrder,
        Cancel: onCancel
    };

    function AddPilotJobOrder(missingPJO : MissingPilotJobOrder) {
        if (adding()) return;

        const opts : PilotJobOrderOptions = JSON.parse(JSON.stringify(template));

        opts.FromPointId = missingPJO.FromPointId();
        opts.ToPointId = missingPJO.ToPointId();

        const
            key = MissingPilotJobOrder.key(missingPJO),
            req = client.Post(opts);
        req.then(json => S.freeze(() => {
            const ts = req.Date,
                pjo = data.PilotJobOrders.update1(ts, json);
            cache.MissingPilotJobOrders.update(ts, [], [key]);
            model.MissingPilotJobOrders.remove(missingPJO);
            onAddPilotJobOrder(pjo);
        })).finally(() => adding(false));
    }
}
export type AddPilotJobOrder = ReturnType<typeof AddPilotJobOrder>;

const MissingPilotJobOrderTypeSortOrder = {
    [MissingPilotJobOrderType.In]    : 0,
    [MissingPilotJobOrderType.Out]   : 1,
    [MissingPilotJobOrderType.Shift] : 2
};

function MissingPJOsDayViewModel(
    date : string,
    missingPJOs : ArraySignal<MissingPilotJobOrder>
) {
    return {
        Date: date,
        DateMoment : moment(date),
        // TH 04/14/2017 -- Hmm... I somehow missed adding a sort to the AddPilotJobOrder popup.
        // Do essentially the same sort as for the main dashboard.
        MissingPJOs: missingPJOs.sort((a, b) =>
               (a.Date().getTime()                          - b.Date().getTime())
            || (MissingPilotJobOrderTypeSortOrder[a.Type()] - MissingPilotJobOrderTypeSortOrder[b.Type()])
            || (a.FromPointId()                             - b.FromPointId())
            || (a.ToPointId()                               - b.ToPointId())
        )
    };
}
export type MissingPJOsDayViewModel = ReturnType<typeof MissingPJOsDayViewModel>;


// WEBPACK FOOTER //
// ./apps/pilots/dispatchdashboard/addpilotjoborder.ts